import React, { useState } from 'react';
import { GitBookFrame, GitBookFrameProps } from '@gitbook/embed/react';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box, IconButton, Paper, Typography } from '@mui/material';

import { useBooleanStateWithToggle } from '@paragon/client-tools';

// The first tab listed is the one the panel opens on. The assistant tab only appears when listed explicitly.
const DOCS_TABS: GitBookFrameProps['tabs'] = ['assistant', 'search', 'docs'];

// `GitBookFrame` re-sends its configuration to the frame whenever these change, and it defaults each one to a new `[]`
// on every render, so stable empty arrays are passed to keep re-renders from re-configuring the frame.
const NONE: [] = [];

export type DocsPanelState = 'closed' | 'minimized' | 'expanded';

interface DocsPanelProps {
  readonly state: DocsPanelState;
  readonly onStateChange: (state: DocsPanelState) => void;
}

export function DocsPanel({ state, onStateChange }: DocsPanelProps) {
  const open = state !== 'closed';
  const minimized = state === 'minimized';

  const [hasOpened, setHasOpened] = useState(open);
  if (open && !hasOpened) setHasOpened(true);

  const toggleMinimized = () => onStateChange(minimized ? 'expanded' : 'minimized');

  // Swapping moves the panel to the opposite bottom corner, uncovering whatever it was in front of.
  const [onLeft, toggleSide] = useBooleanStateWithToggle();
  const sideOffset = { xs: 8, md: 16 };

  // The frame isn't loaded until first opened, then stays mounted while closed so the conversation survives.
  if (!hasOpened) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        display: open ? 'flex' : 'none',
        flexDirection: 'column',
        position: 'fixed',
        ...(onLeft ? { left: sideOffset } : { right: sideOffset }),
        bottom: { xs: 8, md: 48 },
        width: { xs: 'calc(100% - 16px)', sm: 420 },
        height: minimized ? 'auto' : { xs: 'calc(100% - 16px)', md: 'min(720px, calc(100% - 140px))' },
        overflow: 'hidden',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: 2,
          pr: 0.5,
          py: 0.5,
          borderBottom: minimized ? 0 : 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" sx={{ flexGrow: 1, cursor: 'pointer' }} onClick={toggleMinimized}>
          Ask a Question
        </Typography>
        <IconButton
          aria-label={onLeft ? 'Move to right side' : 'Move to left side'}
          onClick={toggleSide}
          sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
        >
          <SwapHorizIcon />
        </IconButton>
        <IconButton aria-label={minimized ? 'Expand' : 'Minimize'} onClick={toggleMinimized}>
          {minimized ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <IconButton aria-label="Close" onClick={() => onStateChange('closed')}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: minimized ? 'none' : 'block',
          flexGrow: 1,
          minHeight: 0,
          '& iframe': { display: 'block', border: 0 },
        }}
      >
        <GitBookFrame tabs={DOCS_TABS} actions={NONE} suggestions={NONE} tools={NONE} trademark={false} />
      </Box>
    </Paper>
  );
}
